import type { EpocusEncounter } from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { useState } from "react";
import { encounterDisplayLabel } from "../../../_lib/encounter-display-label";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../_lib/epocus-palette";
import type { DirectoryMember } from "../../../_lib/use-program-directory";
import { useAssignReviewer } from "../../_lib/use-encounter-assignment";

export type ReassignTarget = {
  reviewerId: string | null;
  reviewerName: string;
  encounters: EpocusEncounter[];
};

const waitingSince = (encounter: EpocusEncounter): string =>
  `Waiting since ${new Date(
    encounter.submittedForReviewAt ?? encounter.date,
  ).toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })}`;

export const ReassignSheet: FC<{
  target: ReassignTarget | null;
  reviewers: readonly DirectoryMember[];
  onClose: () => void;
}> = ({ target, reviewers, onClose }) => {
  const assignReviewer = useAssignReviewer();
  const [assignTo, setAssignTo] = useState("");
  const [moving, setMoving] = useState(false);

  const available = reviewers.filter(
    ({ id }) =>
      id !== target?.reviewerId &&
      !target?.encounters.some(({ performerId }) => performerId === id),
  );

  const close = () => {
    setAssignTo("");
    onClose();
  };

  const moveAll = async () => {
    if (!target || !assignTo) return;
    setMoving(true);
    try {
      for (const encounter of target.encounters) {
        await assignReviewer(encounter.id, assignTo);
      }
      const name = reviewers.find(({ id }) => id === assignTo)?.name ?? "them";
      toast.success(
        `Moved ${target.encounters.length} ${
          target.encounters.length === 1 ? "study" : "studies"
        } to ${name}`,
      );
      close();
    } catch (error) {
      toast.error(
        `Couldn't reassign: ${error instanceof Error ? error.message : "unknown error"}`,
      );
    } finally {
      setMoving(false);
    }
  };

  return (
    <Dialog open={target !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>
        {target?.reviewerId === null
          ? "Assign from the pool"
          : `Reassign ${target?.reviewerName ?? ""}`}
      </DialogTitle>
      <DialogContent>
        <Typography
          style={{
            fontSize: rem(13),
            color: EPOCUS_TEXT_SECONDARY,
            marginBottom: 12,
          }}
        >
          {target?.encounters.length === 0
            ? "Nothing open to move."
            : `${target?.encounters.length} open ${
                target?.encounters.length === 1 ? "study" : "studies"
              }, oldest first.`}
        </Typography>

        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 16,
          }}
        >
          {target?.encounters.map((encounter) => (
            <Box key={encounter.id}>
              <Typography
                style={{
                  fontSize: rem(13),
                  fontWeight: 600,
                  color: EPOCUS_TEXT_PRIMARY,
                }}
              >
                {encounterDisplayLabel(encounter)}
              </Typography>
              <Typography
                style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
              >
                {waitingSince(encounter)}
              </Typography>
            </Box>
          ))}
        </Box>

        <TextField
          select
          id="reassign-reviewer"
          fullWidth
          size="small"
          variant="outlined"
          label="Assign to"
          value={assignTo}
          onChange={(event) => setAssignTo(event.target.value)}
        >
          {available.map(({ id, name }) => (
            <MenuItem key={id} value={id} style={{ fontSize: rem(13) }}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={
            moving || !assignTo || (target?.encounters.length ?? 0) === 0
          }
          onClick={moveAll}
        >
          {target?.reviewerId === null ? "Assign" : "Reassign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
